import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Chart from 'chart.js/auto';
import Highlight from 'react-highlight';
import Scrollspy from './../../components/scrollspy/scrollspy.jsx';

function CodePreview({ code }) {
	return (
		<div className="hljs-container hljs-wrapper rounded-0 rounded-bottom" data-bs-theme="dark">
			<pre>
				<Highlight className="typescript">{code}</Highlight>
			</pre>
		</div>
	);
}

function ChartJs() {
	const [code, setCode] = useState([]);
	const chartRefs = useRef([]);
	const chartsRef = useRef([]);

	useEffect(() => {
		Promise.all(
			Array.from({ length: 6 }, (_, index) =>
				fetch(`/color-admin/admin/react/assets/data/chart-js/code-${index + 1}.json`).then((response) => response.text())
			)
		).then(setCode);
	}, []);

	useEffect(() => {
		const renderCharts = () => {
			chartsRef.current.forEach((chart) => chart.destroy());
			chartsRef.current = [];

			const styles = getComputedStyle(document.body);
			const themeColor = styles.getPropertyValue('--bs-app-theme').trim();
			const themeColorRgb = styles.getPropertyValue('--bs-app-theme-rgb').trim();
			const inverse = styles.getPropertyValue('--bs-inverse').trim();
			const inverseRgb = styles.getPropertyValue('--bs-inverse-rgb').trim();
			const gray500 = styles.getPropertyValue('--bs-gray-500').trim();
			const gray500Rgb = styles.getPropertyValue('--bs-gray-500-rgb').trim();
			const gray300Rgb = styles.getPropertyValue('--bs-gray-300-rgb').trim();
			const gray800Rgb = styles.getPropertyValue('--bs-gray-800-rgb').trim();
			const gray900 = styles.getPropertyValue('--bs-gray-900').trim();
			const bodyBg = styles.getPropertyValue('--bs-body-bg').trim();
			const bodyColor = styles.getPropertyValue('--bs-body-color').trim();
			const borderColor = styles.getPropertyValue('--bs-border-color').trim();
			Chart.defaults.font.family = styles.getPropertyValue('--bs-body-font-family').trim();
			Chart.defaults.font.size = 12;
			Chart.defaults.color = bodyColor;
			Chart.defaults.borderColor = borderColor;
			Chart.defaults.scale.beginAtZero = true;

			const createChart = (index, type, data, options = {}) => {
				const canvas = chartRefs.current[index];
				if (!canvas) return;
				chartsRef.current.push(new Chart(canvas, { type, data, options }));
			};

			createChart(0, 'line', {
				labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
				datasets: [
					{
						label: 'Total Sales',
						data: [12, 19, 4, 5, 2, 3],
						borderColor: themeColor,
						backgroundColor: `rgba(${themeColorRgb}, .2)`,
						borderWidth: 1.5,
						pointBackgroundColor: bodyBg,
						pointBorderWidth: 1.5,
						pointRadius: 4
					}
				]
			});
			createChart(1, 'bar', {
				labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
				datasets: [
					{
						label: 'Total Visitors',
						data: [37, 31, 36, 34, 43, 31],
						backgroundColor: `rgba(${themeColorRgb}, .5)`,
						borderColor: themeColor,
						borderWidth: 1.5
					},
					{
						label: 'New Visitors',
						data: [12, 16, 20, 14, 23, 21],
						backgroundColor: `rgba(${inverseRgb}, .2)`,
						borderColor: `rgba(${inverseRgb}, .65)`,
						borderWidth: 1.5
					}
				]
			});
			createChart(
				2,
				'radar',
				{
					labels: [
						'United States',
						'Canada',
						'Australia',
						'Netherlands',
						'Germany',
						'New Zealand',
						'Singapore'
					],
					datasets: [
						{
							label: 'Mobile',
							data: [65, 59, 90, 81, 56, 55, 40],
							backgroundColor: `rgba(${themeColorRgb}, .2)`,
							borderColor: themeColor,
							borderWidth: 1.5
						},
						{
							label: 'Desktop',
							data: [28, 48, 40, 19, 96, 27, 100],
							backgroundColor: `rgba(${gray500Rgb}, .2)`,
							borderColor: gray500,
							borderWidth: 1.5
						}
					]
				},
				{ maintainAspectRatio: false }
			);
			createChart(
				3,
				'polarArea',
				{
					labels: ['IE', 'Safari', 'Chrome', 'Firefox', 'Opera'],
					datasets: [
						{
							data: [11, 16, 7, 3, 14],
							backgroundColor: [
								`rgba(${themeColorRgb}, .5)`,
								`rgba(${inverseRgb}, .2)`,
								`rgba(${gray300Rgb}, .5)`,
								`rgba(${gray500Rgb}, .5)`,
								`rgba(${gray800Rgb}, .5)`
							],
							borderWidth: 0
						}
					]
				},
				{ maintainAspectRatio: false }
			);
			createChart(
				4,
				'pie',
				{
					labels: ['Total Visitor', 'New Visitor', 'Returning Visitor'],
					datasets: [
						{
							data: [300, 50, 100],
							backgroundColor: [
								`rgba(${themeColorRgb}, .5)`,
								`rgba(${inverseRgb}, .2)`,
								`rgba(${themeColorRgb}, .5)`
							],
							hoverBackgroundColor: [themeColor, inverse, gray900],
							borderWidth: 0
						}
					]
				},
				{ maintainAspectRatio: false }
			);
			createChart(
				5,
				'doughnut',
				{
					labels: ['Total Visitor', 'New Visitor', 'Returning Visitor'],
					datasets: [
						{
							data: [300, 50, 100],
							backgroundColor: [
								`rgba(${themeColorRgb}, .25)`,
								`rgba(${inverseRgb}, .2)`,
								`rgba(${themeColorRgb}, .5)`
							],
							hoverBackgroundColor: [themeColor, inverse, gray900],
							borderWidth: 0
						}
					]
				},
				{ maintainAspectRatio: false }
			);
		};

		renderCharts();
		document.addEventListener('theme-reload', renderCharts);
		return () => {
			document.removeEventListener('theme-reload', renderCharts);
			chartsRef.current.forEach((chart) => chart.destroy());
			chartsRef.current = [];
		};
	}, []);

	const charts = [
		[
			'chartJsLineChart',
			'Line Chart',
			'A line chart is a way of plotting data points on a line. Often, it is used to show trend data, and the comparison of two data sets.',
			'Line Chart'
		],
		[
			'chartJsBarChart',
			'Bar Chart',
			'A bar chart is a way of showing data as bars. It is sometimes used to show trend data, and the comparison of multiple data sets side by side.',
			'Bar Chart'
		],
		[
			'chartJsRadarChart',
			'Radar Chart',
			'A radar chart is a way of showing multiple data points and the variation between them. They are often useful for comparing the points of two or more different data sets.',
			'Radar Chart'
		],
		[
			'chartJsPolarAreaChart',
			'Polar Area Chart',
			'Polar area charts are similar to pie charts, but each segment has the same angle - the radius of the segment differs depending on the value.',
			'Polar Area Chart'
		],
		[
			'chartJsPieChart',
			'Pie Chart',
			'Pie and doughnut charts are probably the most commonly used chart there are. They are divided into segments, the arc of each segment shows the proportional value of each piece of data.',
			'Pie Chart'
		],
		[
			'chartJsDoughnutChart',
			'Doughnut Chart',
			'Pie and doughnut charts are registered under two aliases in the Chart core. Other than their different default value, and different alias, they are exactly the same.',
			'Doughnut Chart'
		]
	];

	return (
		<div className="container">
			<div className="row justify-content-center">
				<div className="col-xl-10">
					<div className="row">
						<div className="col-xl-9">
							<ul className="breadcrumb">
								<li className="breadcrumb-item">
									<Link to="/chart/js">Charts</Link>
								</li>
								<li className="breadcrumb-item active">Chart.js</li>
							</ul>
							<h1 className="page-header">
								Chart.js <small>page header description goes here...</small>
							</h1>
							<hr className="mb-4" />
							<div id="chartJs">
								<h4>Basic Example</h4>
								<p>
									Chart.js provides a simple yet flexible JavaScript charting library for designers
									and developers.
								</p>
							</div>
							{charts.map(([id, title, description, label], index) => (
								<div id={id} className="mb-5" key={id}>
									<div className="card">
										<div className="card-body">
											<h6 className="mb-3">{title}</h6>
											<p>{description}</p>
											<div className={index > 1 ? 'w-300px h-300px mx-auto' : ''}>
												<canvas
													ref={(element) => {
														chartRefs.current[index] = element;
													}}
													aria-label={label}
												/>
											</div>
										</div>
										<CodePreview code={code[index]} />
									</div>
								</div>
							))}
						</div>
						<div className="col-xl-3">
							<Scrollspy introId="chartJs" introLabel="Chart.js" items={charts.map(([id, title]) => ({ id, label: title.toLowerCase() }))} />
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default ChartJs;
